import { logError } from '@/lib/sentry.lib';

import dayjs from 'dayjs';
import type { UndefinedOnPartialDeep } from 'type-fest';

import { isServiceDisabledForTimeWindowSelection } from '@/feature/time-window-update.feature';
import { selectCurrencyCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectDeliveryServicePriceAmount } from '@repo/api-adapters/selectors/service-price.selectors';
import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import { buildServicePricePayload } from '@repo/api-adapters/services/service-price.utils';
import { findDeliveryServiceByServiceId } from '@repo/api-adapters/services/services.api.utils';
import { buildTimeWindowsPayload as buildTimeWindowsApiPayload } from '@repo/api-adapters/services/time-windows.utils';
import type { ServicePriceRequest } from '@repo/hooks';
import type { OrderAsset } from '@repo/types/order';
import type { Service } from '@repo/types/service';
import type { PriceValueField, ServicePriceResponse } from '@repo/types/service-price';
import type { TimeWindow, TimeWindowsPayload } from '@repo/types/time-windows';
import { getAppLocale, getCoworkerProfile } from '@repo/utils';
import type { UseQueryResult } from '@tanstack/react-query';

import { getServicePrice, getServiceTimeZone } from '@/utils/services.utils';
import { formatPrice } from '@/utils/translation.utils';
import { getAppConfig } from '@/utils/window.utils';

interface BuildServiceTimeWindowsPayloadParams {
    activeMonthStartDate: dayjs.Dayjs;
    orderData: OrderAsset | undefined;
    services: SelectedServices;
    service: UndefinedOnPartialDeep<Service>;
}

const formatPriceLabel = (amount: number | undefined, currencyCode: string | undefined): string | undefined => {
    if (amount === undefined || !currencyCode) {
        return;
    }
    return formatPrice(amount, currencyCode);
};

const isTimeWindowPriceMismatch = ({
    timeWindowId,
    timeWindowServicePrices,
    savedServicePrice,
}: {
    timeWindowId: string;
    timeWindowServicePrices: Record<string, string> | undefined;
    savedServicePrice: string | undefined;
}): boolean => {
    const timeWindowPrice = timeWindowServicePrices?.[timeWindowId];
    return timeWindowPrice === undefined || timeWindowPrice !== savedServicePrice;
};

export const isTimeWindowInvalidForUpdate = ({
    alignProposalBlockedTimeWindowIds,
    currentServicePrice,
    timeWindowId,
    timeWindowPrices,
}: {
    alignProposalBlockedTimeWindowIds?: ReadonlySet<string> | undefined;
    currentServicePrice: string | undefined;
    timeWindowId: string;
    timeWindowPrices: Record<string, string>;
}) => {
    return (
        Boolean(alignProposalBlockedTimeWindowIds?.has(timeWindowId)) ||
        isTimeWindowPriceMismatch({
            timeWindowId,
            timeWindowServicePrices: timeWindowPrices,
            savedServicePrice: currentServicePrice,
        })
    );
};

export const buildServiceTimeWindowsPayload = ({
    activeMonthStartDate,
    orderData,
    services,
    service,
}: BuildServiceTimeWindowsPayloadParams): TimeWindowsPayload | undefined => {
    const serviceId = service.serviceId;

    if (!orderData || !serviceId || isServiceDisabledForTimeWindowSelection(serviceId)) {
        return undefined;
    }

    const serviceTimeZone = getServiceTimeZone(service);
    const todayInServiceTimeZone = dayjs().tz(serviceTimeZone).startOf('day').utc();
    const monthStartInServiceTimeZone = dayjs.tz(activeMonthStartDate.format('YYYY-MM-DD'), serviceTimeZone).utc();
    const fromDate = monthStartInServiceTimeZone.isBefore(todayInServiceTimeZone)
        ? todayInServiceTimeZone
        : monthStartInServiceTimeZone;

    return buildTimeWindowsApiPayload({
        orderData,
        services,
        serviceId,
        userId: getCoworkerProfile()?.data?.msalProfile?.mailNickname,
        locale: getAppLocale(),
        fromDate: fromDate.toISOString(),
    });
};

export const buildServicePriceRequests = ({
    orderData,
    services,
    service,
    timeWindows,
}: {
    orderData: OrderAsset | undefined;
    services: SelectedServices;
    service: UndefinedOnPartialDeep<Service>;
    timeWindows: TimeWindow[];
}) => {
    const serviceId = service.serviceId;
    const currentTimeWindowId = service.timeWindow?.timeWindowId;

    if (!service.isDelivery || !orderData || !serviceId) {
        return [];
    }

    const requests: ServicePriceRequest[] = [];

    for (const timeWindow of timeWindows) {
        if (timeWindow.timeWindowId === currentTimeWindowId) {
            continue;
        }

        try {
            const payload = buildServicePricePayload({
                orderData,
                services,
                serviceId,
                selectedTimeWindow: timeWindow,
            });

            if (payload) {
                requests.push({ timeWindowId: timeWindow.timeWindowId, payload });
            }
        } catch (error) {
            logError(error);
        }
    }

    return requests;
};

export const getTimeWindowPriceState = ({
    orderData,
    service,
    timeWindows,
    requests,
    priceQueries,
}: {
    orderData: OrderAsset | undefined;
    service: UndefinedOnPartialDeep<Service>;
    timeWindows: TimeWindow[];
    requests: ServicePriceRequest[];
    priceQueries: Array<UseQueryResult<ServicePriceResponse>>;
}) => {
    const appConfig = getAppConfig();
    const priceValueField: PriceValueField = appConfig.feature.priceExclTax?.enable ? 'priceExclTax' : 'priceInclTax';
    const currentServicePrice = formatPriceLabel(
        getServicePrice(service, priceValueField),
        selectCurrencyCode(orderData)
    );

    const loadingTimeWindowIds = new Set<string>();

    if (!service.isDelivery) {
        const prices: Record<string, string> = {};

        if (currentServicePrice) {
            for (const timeWindow of timeWindows) {
                prices[timeWindow.timeWindowId] = currentServicePrice;
            }
        }

        return {
            timeWindowPrices: prices,
            currentServicePrice,
            hasServicePriceError: false,
            loadingTimeWindowIds,
        };
    }

    const requestedTimeWindowIds = new Set(requests.map((request) => request.timeWindowId));
    const matchedDeliveryNo =
        orderData && service.serviceId
            ? findDeliveryServiceByServiceId(orderData, service.serviceId)?.deliveryNo
            : undefined;
    const prices: Record<string, string> = {};

    for (const [index, request] of requests.entries()) {
        const priceQuery = priceQueries[index];

        if (!priceQuery || priceQuery.isPending) {
            loadingTimeWindowIds.add(request.timeWindowId);
        }

        if (priceQuery?.isError) {
            prices[request.timeWindowId] = '';
            continue;
        }

        const priceAmount = selectDeliveryServicePriceAmount(priceQuery?.data, priceValueField, matchedDeliveryNo);
        const label = priceAmount ? formatPriceLabel(priceAmount.amount, priceAmount.currencyCode) : undefined;

        if (label) {
            prices[request.timeWindowId] = label;
        }
    }

    const currentTimeWindowId = service.timeWindow?.timeWindowId;
    let hasPriceBuildError = false;

    for (const timeWindow of timeWindows) {
        if (timeWindow.timeWindowId === currentTimeWindowId) {
            continue;
        }

        if (!requestedTimeWindowIds.has(timeWindow.timeWindowId)) {
            prices[timeWindow.timeWindowId] = '';
            hasPriceBuildError = true;
        }
    }

    return {
        timeWindowPrices: prices,
        currentServicePrice,
        hasServicePriceError: hasPriceBuildError || priceQueries.some((query) => query.isError),
        loadingTimeWindowIds,
    };
};
